import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import {
  ParamSearchFilter,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import {
  useGetPhoneCallsInfinite,
  useGetPhoneCall,
} from "@/requests/gen/react-query/call-history";
import { useEffect, useMemo, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
  useQueryStates,
} from "nuqs";
import { CallListItem } from "@/components/calling/call-list-item";
import { CallDetail } from "@/components/calling/call-detail";
import { PhoneDialer } from "@/components/calling/phone-dialer";
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_private/_patient-360/calling")({
  component: CallingPage,
});

function CallingPage() {
  const [queryStates] = useQueryStates(dataTableSearchParsers);

  const [selectedCallId, setSelectedCallId] = useQueryState(
    "selected",
    parseAsInteger,
  );

  const [{ duration_min, duration_max, user_ids }] = useQueryStates({
    duration_min: parseAsInteger,
    duration_max: parseAsInteger,
    user_ids: parseAsArrayOf(parseAsString).withDefault([]),
  });

  // Fetch phone calls
  const {
    isFetching,
    data: callsData,
    refetch: refetchCalls,
    fetchNextPage,
  } = useGetPhoneCallsInfinite(
    {
      search: queryStates.search || undefined,
      entity_ids: queryStates.entity_ids,
      limit: 100,
      created_at_start: queryStates.created_at_start ?? undefined,
      created_at_end: queryStates.created_at_end ?? undefined,
      duration_min: duration_min ?? undefined,
      duration_max: duration_max ?? undefined,
      user_ids: user_ids.length ? user_ids : undefined,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.length === 0) return undefined;
          return allPages.length + 1;
        },
      },
    },
  );

  // Fetch selected call details
  const { data: selectedCall, refetch: refetchSelectedCall } = useGetPhoneCall(
    selectedCallId ?? 0,
    {
      query: {
        enabled: selectedCallId !== null,
      },
    },
  );

  // Get items
  const items = useMemo(() => {
    return callsData?.pages.flat() || [];
  }, [callsData]);

  // Scroll
  const viewportRef = useRef<HTMLDivElement | null>(null);

  // Setup infinite scrolling for the list
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const onScroll = () => {
      const nearBottom =
        viewport.scrollTop + viewport.clientHeight >=
        viewport.scrollHeight - 250;
      if (nearBottom && !isFetching) {
        fetchNextPage();
      }
    };

    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", onScroll);
  }, [fetchNextPage, isFetching]);

  const handleStatusChange = () => {
    refetchCalls();
    refetchSelectedCall();
  };

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      <title>Calling - Legrande Health Portal</title>
      <Header crumbs={[{ name: "Calling", link: { to: "/calling" } }]}>
        <Button size="sm" onClick={() => setSelectedCallId(null)}>
          <Phone />
          New Call
        </Button>
      </Header>
      <Card className="flex-1 flex flex-col min-h-0">
        <CardContent className="flex-1 flex px-0 min-h-0">
          {/* Left Column: List */}
          <div
            className={cn(
              "w-full md:w-80 flex flex-col gap-2 md:border-r pr-1 pl-6",
              selectedCallId ? "hidden md:flex" : "flex h-full",
            )}
          >
            {/* Filters */}
            <div className="pr-3 space-y-2">
              <ParamSearchFilter isFetching={isFetching} className="w-full" />
            </div>

            {/* List */}
            <ScrollArea
              className="flex-1 min-h-0 pr-3"
              viewportRef={viewportRef}
            >
              {items.map((call) => (
                <div key={call.phone_call_id}>
                  <CallListItem
                    call={call}
                    isSelected={selectedCallId === call.phone_call_id}
                    onClick={() => setSelectedCallId(call.phone_call_id)}
                  />
                  <Separator className="my-2 mx-4 bg-border/50 last:hidden" />
                </div>
              ))}
              {items.length === 0 && !isFetching && (
                <div className="text-center text-muted-foreground py-8">
                  No calls found
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Right Column: Detail View */}
          <div
            className={cn(
              "flex-1 flex flex-col min-h-0 md:min-w-76",
              !selectedCallId ? "hidden md:flex" : "flex h-full",
            )}
          >
            {selectedCallId && selectedCall && (
              <CallDetail
                call={selectedCall}
                onStatusChange={handleStatusChange}
                setSelectedCallId={setSelectedCallId}
              />
            )}
            {!selectedCallId && (
              <div className="flex-1 flex items-center justify-center p-6">
                <PhoneDialer onCallStarted={() => refetchCalls()} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
